import * as v from 'valibot';
import { ConnectionActor } from './connection.ts';
import { PayloadMatch, PayloadMatchMode, validatePayloadMatch } from './match.ts';
import { type ModelRef, ModelRefSchema } from './model.ts';
import { PolicySpec } from './policy.ts';
import { SharingGrant } from './sharing.ts';
import { WorkspaceSpec, workspaceRepoIssues } from './workspace.ts';
import {
  GITHUB_TRIGGER_EVENT_VALUES,
  JIRA_TRIGGER_EVENT_VALUES,
  LINEAR_TRIGGER_EVENT_VALUES,
  SLACK_TRIGGER_EVENT_VALUES,
} from './trigger-catalog.ts';

/** Deepest nesting the visual builder allows: a parent, a child, a grandchild. */
export const MAX_STEP_LEVEL = 3;

export const RuntimeKind = v.picklist(['isolate', 'container']);
export type RuntimeKind = v.InferOutput<typeof RuntimeKind>;

/**
 * A reference to a connection defined in the control plane (`connections` table).
 * The step config carries only the id and the mount name the generated code-mode
 * client will use — never credentials.
 */
export const ConnectionRef = v.object({
  /** Control-plane connection id. */
  id: v.string(),
  /**
   * The identifier the code-mode client is bound to: `db` becomes
   * `await db.query(...)` in the code the model writes.
   */
  as: v.pipe(v.string(), v.regex(/^[a-z][a-zA-Z0-9_]*$/, 'must be a valid JS identifier')),
  /** Narrow which of the connection's MCP tools this step may reach. */
  tools: v.optional(v.array(v.string())),
  /**
   * Whose identity this step calls with, for a connection that has both a user
   * and a bot credential. Ignored by single-identity connections.
   *
   * This is authored in the workflow document rather than left to the model,
   * because "whose name is on this comment" is not a decision a prompt should
   * be able to make.
   *
   * Left undefined rather than defaulted to `'auto'` so that the overwhelmingly
   * common case adds nothing to the committed JSON, and an `actor` appearing in
   * a diff always means someone made a deliberate choice.
   */
  actor: v.optional(ConnectionActor),
});
export type ConnectionRef = v.InferOutput<typeof ConnectionRef>;

export const SkillRef = v.object({
  /** Versioned skill directory under `/skills`, e.g. `code-standards-v1`. */
  name: v.string(),
});
export type SkillRef = v.InferOutput<typeof SkillRef>;

export const StepStatus = v.picklist([
  'idle',
  'scheduled',
  'running',
  'awaiting_approval',
  'paused_budget',
  'paused_timeout',
  'completed',
  'failed',
  'skipped',
]);
export type StepStatus = v.InferOutput<typeof StepStatus>;

/** Pause thresholds. Flue enforces the wall clock; reef enforces the spend. */
export const StepLimits = v.object({
  /** Wall clock for one continuous run before the step pauses for continuation. */
  maxRuntimeMs: v.optional(v.pipe(v.number(), v.integer(), v.minValue(1000)), 60 * 60 * 1000),
  /** Inference spend in USD cents before the step pauses for continuation. */
  maxSpendCents: v.optional(v.pipe(v.number(), v.integer(), v.minValue(1)), 10_000),
});
export type StepLimits = v.InferOutput<typeof StepLimits>;

/**
 * Payload conditions, shared by every trigger that carries one.
 *
 * Both fields are left undefined rather than defaulted, for the same reason
 * `ConnectionRef.actor` is: the overwhelmingly common trigger has no conditions
 * and should add nothing to the committed JSON, so a `match` appearing in a
 * diff always means somebody wrote a filter.
 */
const payloadMatchFields = {
  /** Conditions on the event payload, evaluated after the event itself matches. */
  match: v.optional(v.array(PayloadMatch)),
  /** Whether every condition must hold (`all`, the default) or just one (`any`). */
  matchMode: v.optional(PayloadMatchMode),
};

export const TriggerSpec = v.variant('type', [
  v.object({
    type: v.literal('manual'),
    /** When true, anyone who can see the workflow may run it, not just editors. */
    triggerFromEveryone: v.optional(v.boolean(), false),
  }),
  v.object({
    type: v.literal('schedule'),
    /**
     * Five-field cron, evaluated in **UTC**. A workflow-level cron starts a run
     * of the whole workflow; `scheduleStep` inside a step is the other, unrelated
     * user of the same parser.
     */
    cron: v.optional(v.string()),
    delayMs: v.optional(v.pipe(v.number(), v.integer(), v.minValue(0))),
  }),
  v.object({
    type: v.literal('linear'),
    events: v.array(v.picklist(LINEAR_TRIGGER_EVENT_VALUES)),
    /** Subscribe by label, project, or team. The router fans a webhook out to matching workflows. */
    labels: v.optional(v.array(v.string()), []),
    projects: v.optional(v.array(v.string()), []),
    teams: v.optional(v.array(v.string()), []),
    /** Only fire when the issue lands in this state, e.g. `triage`. */
    state: v.optional(v.string()),
    ...payloadMatchFields,
  }),
  v.object({
    type: v.literal('github'),
    events: v.array(v.picklist(GITHUB_TRIGGER_EVENT_VALUES)),
    repos: v.optional(v.array(v.string()), []),
    branches: v.optional(v.array(v.string()), []),
    labels: v.optional(v.array(v.string()), []),
    /**
     * GitHub logins, for `issue.assigned`. The reef account's login here is
     * what turns "assign it to reef" into a run.
     *
     * Left undefined rather than defaulted to `[]`, unlike the three fields
     * above it: those predate this one, and a default would rewrite every
     * committed GitHub trigger in the repository to add an empty list nobody
     * typed. The same judgement `match` gets, for the same reason — a field
     * appearing in a diff should mean somebody wrote a filter.
     */
    assignees: v.optional(v.array(v.string())),
    ...payloadMatchFields,
  }),
  /**
   * Jira, which reaches reef as one webhook per site.
   *
   * The narrowings are the ones Jira's payload carries on every issue event and
   * can therefore be precomputed into match keys at commit time (§15a):
   * project, label, issue type, status, assignee. Anything finer — priority, a
   * custom field, a word in the summary — is a payload condition, because the
   * field somebody wants to filter on is reliably the one nobody enumerated.
   */
  v.object({
    type: v.literal('jira'),
    events: v.array(v.picklist(JIRA_TRIGGER_EVENT_VALUES)),
    /** Project keys, e.g. `ENG`. Case-insensitive. */
    projects: v.optional(v.array(v.string()), []),
    labels: v.optional(v.array(v.string()), []),
    /** Issue type names, e.g. `Bug`. Case-insensitive. */
    issueTypes: v.optional(v.array(v.string()), []),
    /**
     * Who the issue is assigned to, for `issue.assigned`. An Atlassian account
     * id, an email address, or a display name — reef emits a match key for all
     * three, because which one a team knows depends on whether their site
     * exposes email at all.
     */
    assignees: v.optional(v.array(v.string()), []),
    /** Only fire when the issue lands in this status, e.g. `In Progress`. */
    status: v.optional(v.string()),
    ...payloadMatchFields,
  }),
  v.object({
    type: v.literal('slack'),
    events: v.array(v.picklist(SLACK_TRIGGER_EVENT_VALUES)),
    channels: v.optional(v.array(v.string()), []),
    emoji: v.optional(v.array(v.string()), []),
    command: v.optional(v.string()),
    ...payloadMatchFields,
  }),
  /**
   * A signed-in-by-bearer-token endpoint anyone can POST to.
   *
   * The other four sources know their own vocabulary, so reef can offer a
   * closed list of events and precompute match keys from the payload's shape.
   * A generic webhook knows nothing: the sender names its own events (in
   * `X-Reef-Event`, or an `event`/`type` field in the body) and the payload is
   * whatever they send. So `events` is free text — empty means every delivery —
   * and `match` is how anything finer gets expressed.
   *
   * The token lives in D1, not here: a credential in a committed document is a
   * credential in the git history. This half is the filter; `POST
   * /api/v1/workflows/:id/webhooks` mints the other half.
   */
  v.object({
    type: v.literal('webhook'),
    /** Event names to accept. Empty (or absent) accepts every delivery. */
    events: v.optional(v.array(v.pipe(v.string(), v.minLength(1))), []),
    ...payloadMatchFields,
  }),
]);
export type TriggerSpec = v.InferOutput<typeof TriggerSpec>;

/**
 * Turn one step into many, from a list the step before it produced.
 *
 * `maxChildrenPerStep` has always bounded fan-out; nothing produced any. The
 * missing half was a way to say "for each of these forty repositories, run this
 * subtree" without authoring forty steps by hand.
 *
 * `source` is a dotted path into the parent step's result, resolved at
 * materialization time — the list is a fact the run discovered, not one the
 * document could have known. `label` names each child from its own item, which
 * is what turns a wall of identical step ids into "failed on acme/legacy-api".
 */
export const FanOutSpec = v.object({
  /** Dotted path into the parent step's output holding the list. */
  source: v.pipe(v.string(), v.minLength(1)),
  /** Dotted path within each item to use as the child's label. */
  label: v.optional(v.string()),
  /**
   * Hard ceiling on children from this step, checked before any are created and
   * additionally bounded by the deployment's own `maxChildrenPerStep`.
   */
  max: v.optional(v.pipe(v.number(), v.integer(), v.minValue(1), v.maxValue(500)), 50),
  /**
   * How many children may be in flight at once.
   *
   * `max` bounds how many children a fan-out *creates*; this bounds how many of
   * them exist at the same moment, and the two are not the same ceiling. Forty
   * children run one at a time is forty sequential agent runs — a fleet-wide
   * migration that takes a week rather than an afternoon. Forty children run at
   * once is forty Durable Objects, forty containers and forty times the spend
   * rate against a daily ceiling that was sized for one, which is the reason
   * this is a width rather than a boolean.
   *
   * Bounded twice: this number is what the author asked for, and the deployment
   * clamps it (`admitFanOutWidth`) to what it is willing to hold. The 16 here is
   * only the point past which a width stops being a width — the number that
   * actually bites is the deployment's, exactly as `max` is bounded again by
   * `maxChildrenPerStep`.
   *
   * **Defaulted to 4.** Every existing `fanOut` step inherits this default
   * without its author choosing it, so it has to be a number that is safe on a
   * deployment that never asked for concurrency at all — which rules out
   * anything near `max`. Four is the smallest width that changes the character
   * of a wide fan-out (forty children in ten waves rather than forty), while a
   * wave's peak claim — four containers, four leases, four concurrent model
   * conversations — stays inside what a single ordinary step already costs
   * several times over. Raising it is one field on the step; lowering it to 1
   * restores exactly the old sequential behaviour, which is what a step that
   * mutates a shared resource per item should do.
   */
  concurrency: v.optional(v.pipe(v.number(), v.integer(), v.minValue(1), v.maxValue(16)), 4),
  /**
   * Keep going when one child fails.
   *
   * Defaulted to true because the alternative — thirty-nine successful
   * migrations abandoned because the fortieth repository had a syntax error —
   * is almost never what anybody meant.
   */
  continueOnError: v.optional(v.boolean(), true),
});
export type FanOutSpec = v.InferOutput<typeof FanOutSpec>;

/**
 * A step that does not end.
 *
 * Every other step runs and finishes. A watch step wakes on its cron, compares
 * what it sees against the baseline it is holding, and either goes back to
 * sleep or trips — and only a trip spawns the children beneath it. That is the
 * difference between a workflow that triages an alert and one that would have
 * noticed first.
 */
export const WatchSpec = v.object({
  /** Five fields, UTC. How often to look. */
  cron: v.string(),
  /** What it is watching for, in its own words — the model evaluates it. */
  until: v.pipe(v.string(), v.minLength(4), v.maxLength(600)),
  /** Stop after this many checks whatever happens, so nothing watches forever. */
  maxChecks: v.optional(v.pipe(v.number(), v.integer(), v.minValue(1), v.maxValue(100_000)), 1000),
  /** Stop after the first trip, rather than continuing to watch. */
  stopOnTrip: v.optional(v.boolean(), false),
});
export type WatchSpec = v.InferOutput<typeof WatchSpec>;

/**
 * A step as authored. `childSteps` nests inline so the whole workflow round-trips
 * through one JSON document in the config repo, which is what makes a workflow
 * reviewable in a pull request.
 */
export interface StepConfig {
  id: string;
  name: string;
  /** Present on every step except roots. Redundant with nesting, but explicit in storage. */
  parentId?: string | null;
  /** 1-based. Enforced `<= MAX_STEP_LEVEL` by `validateWorkflow`. */
  level: number;
  /** Instructions handed to the step's agent. */
  prompt: string;
  runtime?: RuntimeKind;
  /** Hostnames this step may reach. Empty means `network: none`. */
  networkAllowlist?: string[];
  /** Code-mode function names the model may call. Empty means "everything the connections expose". */
  toolsAllowed?: string[];
  policy?: PolicySpec | string;
  mcpConnections?: ConnectionRef[];
  skills?: SkillRef[];
  limits?: StepLimits;
  /** Human approval gate before this step's side effects commit. */
  requiresApproval?: boolean;
  approvalChannel?: string;
  /**
   * Which model this step runs on: `"anthropic/claude-sonnet-5"` as every
   * committed workflow writes it, or `{ provider, model, effort }` when the
   * step wants to say more than a name. Resolved against the acting person's
   * preference at step render — see `resolveModel` in `./model.ts`.
   */
  model?: ModelRef;
  /**
   * The repositories this step stands in, and the named environment they and
   * their secrets come from.
   *
   * Checked out when the step starts, each under its own `path`, through the
   * `git` connection's own `clone` — there is no second credential path and no
   * second reach check. Inheritance **tightens**: an ancestor's set is the
   * ceiling for its whole subtree, which is the opposite direction from
   * `mcpConnections` and is argued out in `mergeWorkspace`.
   */
  workspace?: WorkspaceSpec;
  /** Materialize `childSteps` once per item in a list the parent produced. */
  fanOut?: FanOutSpec;
  /** Make this a watcher: it wakes on a cron and only spawns children on a trip. */
  watch?: WatchSpec;
  childSteps?: StepConfig[];
}

// `GenericSchema<unknown, StepConfig>` rather than `GenericSchema<StepConfig>`:
// the parsed *output* is a StepConfig, but the accepted *input* is looser
// because several fields carry defaults.
export const StepConfigSchema: v.GenericSchema<unknown, StepConfig> = v.object({
  id: v.pipe(v.string(), v.minLength(1)),
  name: v.pipe(v.string(), v.minLength(1)),
  parentId: v.optional(v.nullable(v.string())),
  level: v.pipe(v.number(), v.integer(), v.minValue(1), v.maxValue(MAX_STEP_LEVEL)),
  prompt: v.string(),
  runtime: v.optional(RuntimeKind),
  networkAllowlist: v.optional(v.array(v.string())),
  toolsAllowed: v.optional(v.array(v.string())),
  policy: v.optional(v.union([PolicySpec, v.string()])),
  mcpConnections: v.optional(v.array(ConnectionRef)),
  skills: v.optional(v.array(SkillRef)),
  limits: v.optional(StepLimits),
  requiresApproval: v.optional(v.boolean()),
  approvalChannel: v.optional(v.string()),
  model: v.optional(ModelRefSchema),
  workspace: v.optional(WorkspaceSpec),
  fanOut: v.optional(FanOutSpec),
  watch: v.optional(WatchSpec),
  childSteps: v.optional(v.array(v.lazy(() => StepConfigSchema))),
});

export const WorkflowConfig = v.object({
  id: v.pipe(v.string(), v.minLength(1)),
  name: v.pipe(v.string(), v.minLength(1)),
  description: v.optional(v.string()),
  /** Path in the config repo this document is committed to. */
  configPath: v.optional(v.string()),
  ownerId: v.string(),
  sharing: v.optional(v.array(SharingGrant), []),
  /** Workflow-level default, inherited by every step that does not override it. */
  runtime: v.optional(RuntimeKind, 'isolate'),
  networkAllowlist: v.optional(v.array(v.string()), []),
  /** Connections every step inherits. Steps may add, never remove. */
  mcpConnections: v.optional(v.array(ConnectionRef), []),
  policy: v.optional(v.union([PolicySpec, v.string()])),
  skills: v.optional(v.array(SkillRef), []),
  limits: v.optional(StepLimits),
  /**
   * The repository set the whole workflow works in.
   *
   * Here as well as on a step because "this workflow is about `acme/web` and
   * `acme/contracts`" is a fact about the document, and stating it once at the
   * top is what makes every step's ceiling visible on the first screen rather
   * than three levels down. Steps narrow it; none of them widens it.
   */
  workspace: v.optional(WorkspaceSpec),
  triggers: v.optional(v.array(TriggerSpec), []),
  steps: v.array(StepConfigSchema),
  /** Bumped on every write-back so the UI can detect a stale editor. */
  revision: v.optional(v.pipe(v.number(), v.integer(), v.minValue(0)), 0),
});
export type WorkflowConfig = v.InferOutput<typeof WorkflowConfig>;

export interface WorkflowValidationIssue {
  path: string;
  message: string;
}

/**
 * Structural checks the valibot schema cannot express on its own: depth,
 * level/nesting agreement, unique ids, and identifier collisions among the
 * connection mounts a step will end up with after inheritance.
 */
/**
 * Five fields, separated by whitespace.
 *
 * Shared by the schedule trigger and the watch step so the two cannot disagree
 * about what a cron is — the parsing itself lives in the Worker, and this is
 * only the shape check that can be made without it.
 */
function isFiveFieldCron(expression: string): boolean {
  return expression.trim().split(/\s+/).length === 5;
}

export function validateWorkflow(workflow: WorkflowConfig): WorkflowValidationIssue[] {
  const issues: WorkflowValidationIssue[] = [];
  const seenIds = new Set<string>();

  const walk = (steps: readonly StepConfig[], depth: number, parentId: string | null, path: string) => {
    steps.forEach((step, index) => {
      const here = `${path}[${index}]`;

      if (seenIds.has(step.id)) {
        issues.push({ path: here, message: `duplicate step id "${step.id}"` });
      }
      seenIds.add(step.id);

      if (depth > MAX_STEP_LEVEL) {
        issues.push({
          path: here,
          message: `step "${step.id}" is nested ${depth} levels deep; the maximum is ${MAX_STEP_LEVEL}`,
        });
      }
      if (step.level !== depth) {
        issues.push({
          path: here,
          message: `step "${step.id}" declares level ${step.level} but is nested at depth ${depth}`,
        });
      }
      if ((step.parentId ?? null) !== parentId) {
        issues.push({
          path: here,
          message: `step "${step.id}" declares parentId ${JSON.stringify(step.parentId ?? null)} but is nested under ${JSON.stringify(parentId)}`,
        });
      }

      const mounts = new Set<string>();
      for (const connection of step.mcpConnections ?? []) {
        if (mounts.has(connection.as)) {
          issues.push({
            path: `${here}.mcpConnections`,
            message: `step "${step.id}" mounts two connections as "${connection.as}"`,
          });
        }
        mounts.add(connection.as);
      }

      /*
       * A workspace that names nothing is a step declaring it needs a checkout
       * and giving the runtime no way to make one. It parses, it deploys, and
       * the step then fails on "this run has no checkout of anything" — which
       * reads as a runtime fault rather than as the missing line it is.
       */
      if (step.workspace && step.workspace.repos.length === 0 && !step.workspace.environment) {
        issues.push({
          path: `${here}.workspace`,
          message: `step "${step.id}" declares a workspace with no repositories and no environment`,
        });
      }
      for (const message of workspaceRepoIssues(step.workspace?.repos ?? [])) {
        issues.push({ path: `${here}.workspace.repos`, message: `step "${step.id}": ${message}` });
      }

      /*
       * Both new step kinds mean "run the children beneath me", so a step that
       * declares one and has no children is a step that can never do anything.
       * That parses, deploys, and quietly does nothing at 3am — which is the
       * failure worth catching in a diff rather than in a postmortem.
       */
      if (step.fanOut && !step.childSteps?.length) {
        issues.push({
          path: `${here}.fanOut`,
          message: `step "${step.id}" fans out but has no child steps to fan out into`,
        });
      }
      if (step.watch && !step.childSteps?.length) {
        issues.push({
          path: `${here}.watch`,
          message: `step "${step.id}" watches but has no child steps to run when it trips`,
        });
      }

      // A step is one or the other. Fanning out a watcher would mean forty
      // long-lived agents from one document, which is a fleet nobody sized.
      if (step.fanOut && step.watch) {
        issues.push({
          path: here,
          message: `step "${step.id}" is both a fan-out and a watch; it can only be one`,
        });
      }

      /*
       * A fan-out materializes its children one level down, so a fan-out at the
       * deepest level has nowhere to put them. Caught here rather than at
       * materialization time, where the run is already going.
       */
      if (step.fanOut && depth >= MAX_STEP_LEVEL) {
        issues.push({
          path: `${here}.fanOut`,
          message: `step "${step.id}" fans out at level ${depth}; its children would exceed the ${MAX_STEP_LEVEL}-level limit`,
        });
      }

      if (step.watch && !isFiveFieldCron(step.watch.cron)) {
        issues.push({
          path: `${here}.watch.cron`,
          message: `step "${step.id}" has a watch cron that is not five fields`,
        });
      }

      if (step.childSteps?.length) {
        walk(step.childSteps, depth + 1, step.id, `${here}.childSteps`);
      }
    });
  };

  walk(workflow.steps, 1, null, 'steps');

  if (workflow.workspace && workflow.workspace.repos.length === 0 && !workflow.workspace.environment) {
    issues.push({
      path: 'workspace',
      message: 'this workflow declares a workspace with no repositories and no environment',
    });
  }
  for (const message of workspaceRepoIssues(workflow.workspace?.repos ?? [])) {
    issues.push({ path: 'workspace.repos', message });
  }

  issues.push(...validateTriggers(workflow));
  return issues;
}

/**
 * Checks on triggers that the schema cannot express.
 *
 * All of these describe a trigger that parses but can never fire, which is the
 * failure mode worth catching early: "my workflow never ran" and "my workflow
 * never received the event" look identical from the outside, and only one of
 * them is reef's fault.
 */
function validateTriggers(workflow: WorkflowConfig): WorkflowValidationIssue[] {
  const issues: WorkflowValidationIssue[] = [];

  /*
   * A second manual trigger is not a second way to run the workflow — there is
   * only one Run button, and `triggerFromEveryone` is read with `.some()`, so
   * two of them that disagree quietly widen access to whichever says yes. One,
   * or none.
   */
  const manual = workflow.triggers.filter((trigger) => trigger.type === 'manual');
  if (manual.length > 1) {
    issues.push({
      path: 'triggers',
      message: 'a workflow has one manual trigger at most — running it by hand is a single door',
    });
  }

  workflow.triggers.forEach((trigger, index) => {
    const here = `triggers[${index}]`;

    if (trigger.type === 'schedule') {
      if (!trigger.cron && trigger.delayMs === undefined) {
        issues.push({ path: here, message: 'a schedule trigger needs a cron expression' });
      }
      if (trigger.cron && !isFiveFieldCron(trigger.cron)) {
        issues.push({
          path: `${here}.cron`,
          message: `"${trigger.cron}" is not a five-field cron expression`,
        });
      }
    }

    if ('events' in trigger && trigger.type !== 'webhook' && trigger.events.length === 0) {
      issues.push({
        path: `${here}.events`,
        message: `a ${trigger.type} trigger with no events subscribes to nothing`,
      });
    }

    if ('match' in trigger && trigger.match?.length) {
      trigger.match.forEach((condition, conditionIndex) => {
        for (const message of validatePayloadMatch(condition)) {
          issues.push({ path: `${here}.match[${conditionIndex}]`, message });
        }
      });
    }
  });

  return issues;
}

export function parseWorkflow(input: unknown): WorkflowConfig {
  const workflow = v.parse(WorkflowConfig, input);
  const issues = validateWorkflow(workflow);
  if (issues.length > 0) {
    throw new WorkflowValidationError(issues);
  }
  return workflow;
}

export class WorkflowValidationError extends Error {
  readonly issues: WorkflowValidationIssue[];
  constructor(issues: WorkflowValidationIssue[]) {
    super(`invalid workflow: ${issues.map((i) => `${i.path}: ${i.message}`).join('; ')}`);
    this.name = 'WorkflowValidationError';
    this.issues = issues;
  }
}

/** Depth-first walk yielding every step with its ancestor chain, roots first. */
export function* walkSteps(
  workflow: WorkflowConfig,
): Generator<{ step: StepConfig; ancestors: StepConfig[] }> {
  function* recurse(steps: readonly StepConfig[], ancestors: StepConfig[]): Generator<{
    step: StepConfig;
    ancestors: StepConfig[];
  }> {
    for (const step of steps) {
      yield { step, ancestors };
      if (step.childSteps?.length) {
        yield* recurse(step.childSteps, [...ancestors, step]);
      }
    }
  }
  yield* recurse(workflow.steps, []);
}

export function findStep(workflow: WorkflowConfig, stepId: string): StepConfig | null {
  for (const { step } of walkSteps(workflow)) {
    if (step.id === stepId) return step;
  }
  return null;
}

export function stepAncestry(workflow: WorkflowConfig, stepId: string): StepConfig[] | null {
  for (const { step, ancestors } of walkSteps(workflow)) {
    if (step.id === stepId) return [...ancestors, step];
  }
  return null;
}
